import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { inventoryApi } from '../../api/inventory-api-instance';
import { ErrorState } from '../../components/ErrorState';
import { PageLoadingState } from '../../components/PageLoadingState';
import { StatusBadge } from '../../components/StatusBadge';
import { humanizeIdentifier } from '../../lib/formatting';

/** Devices assigned to the site, with any attention reason. */
export function SiteInventoryTab({ siteId }: { siteId: string }) {
  const items = useQuery({
    queryKey: ['items-for-site', siteId],
    queryFn: () => inventoryApi.listItems({ siteId, pageSize: 5000 }),
    enabled: Boolean(siteId),
  });
  const parts = useQuery({ queryKey: ['parts'], queryFn: () => inventoryApi.listParts() });
  const partTypes = new Map((parts.data ?? []).map((part) => [part.id, part.type]));
  const siteItems = items.data?.data ?? [];

  if (items.isLoading) return <PageLoadingState label="Loading devices…" />;
  if (items.isError) {
    return (
      <ErrorState
        title="Devices could not be loaded"
        rpcName="ListItems"
        onRetry={() => void items.refetch()}
      />
    );
  }

  return (
    <section aria-label="Devices at this site">
      {siteItems.length === 0 ? (
        <p className="empty-note">No devices are assigned to this site.</p>
      ) : (
        <>
          <p className="field-hint">
            {siteItems.length.toLocaleString()} assigned ·{' '}
            {siteItems.filter((item) => item.needsAttention).length.toLocaleString()} need attention
            ·{' '}
            <Link to={`/inventory?assignedSiteId=${encodeURIComponent(siteId)}`}>
              Open in Inventory
            </Link>
          </p>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Device ID</th>
                  <th scope="col">Type</th>
                  <th scope="col">Status</th>
                  <th scope="col">Attention</th>
                </tr>
              </thead>
              <tbody>
                {siteItems.map((item) => {
                  const type = partTypes.get(item.partId);
                  return (
                    <tr key={item.id}>
                      <th scope="row">
                        <Link to={`/inventory/${encodeURIComponent(item.id)}`} className="mono">
                          {item.id}
                        </Link>
                      </th>
                      <td>{type ? humanizeIdentifier(type) : '—'}</td>
                      <td>
                        <StatusBadge status={item.status} />
                      </td>
                      <td>{item.needsAttention ? (item.attentionReason ?? 'Yes') : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
